import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Check } from "lucide-react";
import SEO from "@/components/SEO";
import { ShieldWm } from "@/components/Watermark";
import { FadeUp, MaskedLine, Eyebrow } from "@/components/Reveal";
import { QuoteDateField, QuoteTimeField } from "@/components/QuoteDateTime";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TIPOLOGIE = [
  "Trasferimento privato",
  "Aeroporto",
  "Business",
  "Evento / Cerimonia",
  "Lunga distanza",
  "Servizio personalizzato",
  "Altro",
];

const INITIAL = {
  nome: "",
  email: "",
  telefono: "",
  azienda: "",
  data: "",
  ora: "",
  partenza: "",
  destinazione: "",
  passeggeri: "1",
  bagagli: "",
  tipologia: "",
  viaggio: "Sola andata",
  note: "",
  privacy: false,
  website: "",
};

function validate(f) {
  const e = {};
  if (!f.nome || f.nome.trim().length < 2) e.nome = "Inserisci nome e cognome.";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Inserisci un'email valida.";
  if (!f.telefono || f.telefono.replace(/\D/g, "").length < 6) e.telefono = "Inserisci un numero di telefono valido.";
  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
  if (!f.data) e.data = "Inserisci la data del viaggio.";
  else if (f.data < todayStr) e.data = "Inserisci una data valida.";
  if (!f.ora) e.ora = "Inserisci l'orario di partenza.";
  if (!f.partenza || f.partenza.trim().length < 2) e.partenza = "Inserisci il luogo di partenza.";
  if (!f.destinazione || f.destinazione.trim().length < 2) e.destinazione = "Inserisci la destinazione.";
  const p = parseInt(f.passeggeri, 10);
  if (!p || p < 1 || p > 50) e.passeggeri = "Indica il numero di passeggeri (1-50).";
  if (f.bagagli && (parseInt(f.bagagli, 10) < 0 || parseInt(f.bagagli, 10) > 50)) e.bagagli = "Numero di bagagli non valido.";
  if (!f.tipologia) e.tipologia = "Seleziona il tipo di servizio.";
  if (!f.privacy) e.privacy = "È necessario accettare la Privacy Policy.";
  return e;
}

const SectionLabel = ({ children }) => (
  <div className="mt-16 flex items-center gap-4 first:mt-0">
    <span className="h-px w-8 bg-gold/60" />
    <span className="label-cap">{children}</span>
  </div>
);

const Field = ({ label, required, error, children, className = "" }) => (
  <div className={`${error ? "field-error" : ""} ${className}`}>
    <label className="label-cap mb-2 block !text-[9px]">
      {label} {required && <span className="text-gold">*</span>}
    </label>
    {children}
    {error && <p className="error-msg" data-testid="field-error">{error}</p>}
  </div>
);

export default function Preventivo() {
  const [f, setF] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);
  const [globalError, setGlobalError] = useState("");

  const set = (k) => (e) => setF((v) => ({ ...v, [k]: e.target.value }));

  const submit = async (ev) => {
    ev.preventDefault();
    setGlobalError("");
    const errs = validate(f);
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSending(true);
    try {
      await axios.post(`${API}/preventivo`, {
        nome: f.nome.trim(),
        email: f.email.trim(),
        telefono: f.telefono.trim(),
        azienda: f.azienda.trim() || null,
        data: f.data,
        ora: f.ora,
        partenza: f.partenza.trim(),
        destinazione: f.destinazione.trim(),
        passeggeri: parseInt(f.passeggeri, 10),
        bagagli: f.bagagli ? parseInt(f.bagagli, 10) : null,
        tipologia: f.tipologia,
        viaggio: f.viaggio,
        note: f.note.trim() || null,
        privacy: f.privacy,
        website: f.website,
      });
      setDone(true);
      if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
      else window.scrollTo(0, 0);
    } catch (err) {
      setGlobalError(
        err.response && err.response.status === 429
          ? "Hai inviato troppe richieste. Riprova più tardi o contattaci direttamente."
          : "Si è verificato un errore nell'invio. Riprova tra qualche istante."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <SEO
        title="Richiedi un Preventivo | Zaichauffer – Autista Privato"
        description="Richiedi un preventivo personalizzato per il tuo trasferimento con autista privato: aeroporti, città, business ed eventi in tutta Italia e in Europa. Khalid ti ricontatterà al più presto."
      />

      <section className="relative isolate mx-auto max-w-7xl overflow-hidden px-6 pb-24 pt-40 md:pb-36 md:pt-52" data-testid="preventivo-page">
      <ShieldWm className="-z-10 -right-24 top-10 w-[400px] opacity-[0.04]" />
        <FadeUp>
          <Eyebrow>Richiedi preventivo</Eyebrow>
        </FadeUp>
        <h1 className="mt-10 font-serif text-4xl leading-[1.08] tracking-tight text-bone sm:text-5xl lg:text-6xl">
          <MaskedLine>RICHIEDI IL TUO PREVENTIVO</MaskedLine>
        </h1>
        <FadeUp delay={0.2}>
          <p className="mt-8 max-w-2xl text-[15px] font-light leading-relaxed text-mute">
            Compila il modulo. Khalid riceverà direttamente la tua richiesta e
            ti ricontatterà per definire il servizio.
          </p>
        </FadeUp>

        {done ? (
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            data-testid="quote-success-message"
            className="mt-16 max-w-3xl border border-gold/30 bg-coal p-12 text-center md:p-20"
          >
            <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-gold/60">
              <Check size={22} className="text-gold" />
            </span>
            <h2 className="mt-8 font-serif text-2xl leading-snug text-bone md:text-3xl">
              Richiesta inviata con successo.
            </h2>
            <p className="mt-4 text-[15px] font-light leading-relaxed text-mute">
              Grazie per aver contattato Zaichauffer. Khalid ti ricontatterà
              al più presto.
            </p>
          </motion.div>
        ) : (
          <form
            data-testid="quote-form"
            onSubmit={submit}
            noValidate
            className="mt-16 max-w-3xl"
          >
            <SectionLabel>Dati del cliente</SectionLabel>
            <div className="mt-8 grid gap-x-10 gap-y-10 sm:grid-cols-2">
              <Field label="Nome e cognome" required error={errors.nome}>
                <input data-testid="quote-form-name-input" className="field" type="text" placeholder="Mario Rossi" value={f.nome} onChange={set("nome")} />
              </Field>
              <Field label="Email" required error={errors.email}>
                <input data-testid="quote-form-email-input" className="field" type="email" placeholder="mario@esempio.it" value={f.email} onChange={set("email")} />
              </Field>
              <Field label="Numero di telefono" required error={errors.telefono}>
                <input data-testid="quote-form-phone-input" className="field" type="tel" placeholder="+39 333 000 0000" value={f.telefono} onChange={set("telefono")} />
              </Field>
              <Field label="Azienda (facoltativo)" error={errors.azienda}>
                <input data-testid="quote-form-company-input" className="field" type="text" placeholder="Nome azienda" value={f.azienda} onChange={set("azienda")} />
              </Field>
            </div>

            <SectionLabel>Dettagli del viaggio</SectionLabel>
            <div className="mt-8 grid gap-x-10 gap-y-10 sm:grid-cols-2">
              <Field label="Data del viaggio" required error={errors.data}>
                <QuoteDateField lang="it" value={f.data} onChange={(v) => setF((s) => ({ ...s, data: v }))} />
              </Field>
              <Field label="Orario di partenza" required error={errors.ora}>
                <QuoteTimeField lang="it" value={f.ora} onChange={(v) => setF((s) => ({ ...s, ora: v }))} />
              </Field>
              <Field label="Luogo di partenza" required error={errors.partenza}>
                <input data-testid="quote-form-pickup-input" className="field" type="text" placeholder="Indirizzo, città o aeroporto" value={f.partenza} onChange={set("partenza")} />
              </Field>
              <Field label="Destinazione" required error={errors.destinazione}>
                <input data-testid="quote-form-destination-input" className="field" type="text" placeholder="Indirizzo, città o aeroporto" value={f.destinazione} onChange={set("destinazione")} />
              </Field>
              <Field label="Numero di passeggeri" required error={errors.passeggeri}>
                <input data-testid="quote-form-passengers-input" className="field" type="number" min="1" max="50" value={f.passeggeri} onChange={set("passeggeri")} />
              </Field>
              <Field label="Numero di bagagli" error={errors.bagagli}>
                <input data-testid="quote-form-luggage-input" className="field" type="number" min="0" max="50" placeholder="Se noto" value={f.bagagli} onChange={set("bagagli")} />
              </Field>
            </div>

            <div className="mt-10">
              <label className="label-cap mb-3 block !text-[9px]">Tipologia di viaggio</label>
              <div className="flex flex-wrap gap-3">
                {["Sola andata", "Andata e ritorno"].map((v) => (
                  <button
                    key={v}
                    type="button"
                    data-testid={`quote-form-trip-${v === "Sola andata" ? "andata" : "ritorno"}`}
                    onClick={() => setF((s) => ({ ...s, viaggio: v }))}
                    className={`border px-5 py-3 text-[10px] tracking-[0.18em] uppercase transition-colors duration-300 ${
                      f.viaggio === v
                        ? "border-gold text-gold"
                        : "border-white/15 text-bone/60 hover:border-gold/50"
                    }`}
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>

            <SectionLabel>Tipo di servizio</SectionLabel>
            <div className={`mt-8 ${errors.tipologia ? "field-error" : ""}`}>
              <Field label="Seleziona il servizio" required error={errors.tipologia}>
                <select data-testid="quote-form-service-select" className="field" value={f.tipologia} onChange={set("tipologia")}>
                  <option value="">Seleziona un servizio…</option>
                  {TIPOLOGIE.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </Field>
            </div>

            <SectionLabel>Note</SectionLabel>
            <div className="mt-8">
              <Field label="Esigenze particolari, fermate intermedie, voli e altre informazioni utili" error={errors.note}>
                <textarea
                  data-testid="quote-form-notes-input"
                  className="field"
                  placeholder="Indicaci eventuali esigenze particolari, fermate intermedie, orari, voli o altre informazioni utili."
                  value={f.note}
                  onChange={set("note")}
                />
              </Field>
            </div>

            {/* Honeypot antispam — invisibile per le persone */}
            <div className="hidden" aria-hidden="true">
              <label>Sito web</label>
              <input type="text" name="website" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} />
            </div>

            <div className="mt-14">
              <label className="flex cursor-pointer items-start gap-4">
                <input
                  data-testid="quote-form-privacy-checkbox"
                  type="checkbox"
                  className="lux-check mt-0.5"
                  checked={f.privacy}
                  onChange={(e) => setF((s) => ({ ...s, privacy: e.target.checked }))}
                />
                <span className="text-[13px] font-light leading-relaxed text-bone/75">
                  Ho letto e accetto la{" "}
                  <a href="/privacy-policy" className="text-gold underline-offset-4 hover:underline">
                    Privacy Policy
                  </a>{" "}
                  e acconsento al trattamento dei dati personali per la
                  gestione della richiesta. *
                </span>
              </label>
              {errors.privacy && <p className="error-msg" data-testid="privacy-error">{errors.privacy}</p>}
            </div>

            {globalError && (
              <div data-testid="quote-error-message" role="alert" className="mt-8 border border-red-900/50 bg-red-950/30 px-6 py-4 text-sm text-red-200">
                {globalError}
              </div>
            )}

            <button data-testid="quote-form-submit-button" type="submit" disabled={sending} className="btn-solid mt-12 disabled:cursor-wait disabled:opacity-60">
              {sending ? "Invio in corso…" : "Invia richiesta"}
            </button>
            <p className="mt-6 text-xs font-light text-mute/70">
              Campi contrassegnati con <span className="text-gold">*</span> obbligatori.
            </p>
          </form>
        )}
      </section>
    </>
  );
}
